import '../styles/themes/global.scss'

import { useContext } from 'react'

import { DarkModePreference } from './darkModeTypes'
import { ThemeContext } from './Theme'
import { themeSupportsDarkMode } from './themeCapabilities'
import { DarkMode, DesignLanguage, Theme, ThemeProps } from './types'

export const useTheme = (): ThemeProps => {
  return useContext(ThemeContext)
}

export const useThemeClass = (): string => {
  const {
    theme: { selected },
  } = useTheme()

  return `theme${selected}`
}

/**
 *  This returns classes that have themes embedded so you don't have to do for eg.
 *
 *  ```
 *    const wrapperClasses = clsx(isCash && styles.cashWrapper, styles.wrapper)
 *  ```
 *
 *  Just do this instead
 *  ```
 *    import styles from style.module.css
 *    const themedClasses = useThemePerClass(styles)
 *    <div class={themedClasses.wrapper} />
 *  ```
 *
 *  In your css, you can use a themed specific styling, for eg.
 *  ```
 *    .Unicorn_wrapper { display: none; }
 *  ```
 *
 *  Or, have two themes at once
 *  ```
 *  .wrapper { display: block; }
 *  .Unicorn_wrapper { display: inline-flex; }
 *  ```
 */
export const useThemePerClass = (classNames: { [key: string]: string }): { [key: string]: string } => {
  const {
    theme: { selected },
  } = useTheme()

  return Object.entries(classNames).reduce((accumulator: { [key: string]: string }, [key, value]) => {
    const strippedKey = key.replace(`${selected}_`, '').trim()

    if (typeof accumulator[strippedKey] === 'undefined') {
      accumulator[strippedKey] = value
    } else {
      accumulator[strippedKey] += ` ${value}`
    }

    return accumulator
  }, {})
}

/**
 *  This returns classes that have design language embedded so you don't have to do for eg.
 *
 *  ```
 *    const wrapperClasses = clsx(isArcade && styles.arcadeWrapper, styles.wrapper)
 *  ```
 *
 *  Just do this instead
 *  ```
 *    import styles from style.module.css
 *    const designLanguageClasses = useDesignLanguagePerClass(styles)
 *    <div class={designLanguageClasses.wrapper} />
 *  ```
 *
 *  In your css, you can use a design language specific styling, for eg.
 *  ```
 *    .Arcade_wrapper { display: none; }
 *  ```
 *
 *  Or, have two design languages at once
 *  ```
 *  .wrapper { display: block; }
 *  .Arcade_wrapper { display: inline-flex; }
 *  ```
 */
export const useDesignLanguagePerClass = (classNames: { [key: string]: string }): { [key: string]: string } => {
  const {
    theme: { designLanguage },
  } = useTheme()

  return Object.entries(classNames).reduce((accumulator: { [key: string]: string }, [key, value]) => {
    const strippedKey = key.replace(`${designLanguage}_`, '').trim()

    if (typeof accumulator[strippedKey] === 'undefined') {
      accumulator[strippedKey] = value
    } else {
      accumulator[strippedKey] += ` ${value}`
    }

    return accumulator
  }, {})
}

/**
 * @deprecated use `useIsTheme` instead
 */
export const useIsAPTheme = (): boolean => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === Theme.AP
}

/**
 * @deprecated use `useIsTheme` instead
 */
export const useIsCashTheme = (): boolean => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === Theme.Cash
}

export const useGetSelectedTheme = (): Theme => {
  const {
    theme: { selected },
  } = useTheme()

  return selected
}

export const useIsTheme = (theme: Theme): theme is Theme => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === theme
}

export const useIsDarkMode = (): boolean => {
  const {
    theme: { isDarkModeActive, selected },
  } = useTheme()

  return isDarkModeActive && themeSupportsDarkMode(selected)
}

export const useIsDesignLanguage = (designLanguage: DesignLanguage): designLanguage is DesignLanguage => {
  const {
    theme: { designLanguage: currentLayout },
  } = useTheme()

  return currentLayout === designLanguage
}

export const useGetSelectedDesignLanguage = (): DesignLanguage => {
  const {
    theme: { designLanguage },
  } = useTheme()

  return designLanguage
}

/**
 * Hook for managing dark mode with cookie persistence
 * @returns object with current dark mode state and setter function
 */
export const useDarkMode = (): { isDarkMode: boolean; setDarkMode: (darkMode: DarkMode) => void } => {
  const {
    theme: { setDarkMode },
  } = useTheme()

  return {
    isDarkMode: useIsDarkMode(),
    setDarkMode,
  }
}

/**
 * Hook for managing dark mode preferences including system mode
 * Note: This hook does not read cookie preferences. Apps should pass
 * getDarkModePreference to ThemeProvider to handle cookie reading.
 * @returns object with dark mode state and setter functions
 */
export const useDarkModePreference = (): {
  isDarkMode: boolean
  setDarkMode: () => void
  setLightMode: () => void
  setSystemMode: () => void
  setPreference: (preference: DarkModePreference) => void
} => {
  const {
    theme: { setDarkMode: setThemeDarkMode },
  } = useTheme()

  return {
    isDarkMode: useIsDarkMode(),
    setDarkMode: () => setThemeDarkMode(DarkMode.Dark),
    setLightMode: () => setThemeDarkMode(DarkMode.Light),
    setSystemMode: () => setThemeDarkMode(DarkMode.System),
    setPreference: (pref: DarkModePreference) => {
      if (pref === 'dark') {
        setThemeDarkMode(DarkMode.Dark)
      } else if (pref === 'light') {
        setThemeDarkMode(DarkMode.Light)
      } else {
        setThemeDarkMode(DarkMode.System)
      }
    },
  }
}
